/**
 * EcologicalMapStyles.js
 * 
 * Centralized sx prop styles for Ecological Map page components
 * Migrated from src/styles/pages/ecological.css
 * 
 * STRATEGY: Migrate reusable panel/component styles to sx props
 * KEEP IN CSS: Critical absolute positioning for Mapbox integration (#map positioning)
 * 
 * Pattern: Export functions that return sx objects, accepting theme parameter
 */

import { alpha } from '@mui/material/styles';

// ===== PANEL CONTAINERS (Floating) =====
// Note: Absolute positioning kept in CSS for Mapbox, these are internal styles

export const getPanelSubSectionSx = (theme) => ({
  background: theme.palette.background.paper,
  borderRadius: 2,
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1), 0 1px 3px rgba(0, 0, 0, 0.06)',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  maxHeight: 'inherit',
  border: `1px solid ${theme.palette.grey[100]}`
});

export const getSiteBarSubSectionSx = (theme) => ({
  ...getPanelSubSectionSx(theme),
  overflowY: 'auto',
  maxHeight: 'calc(100vh - 100px)',
  '&::-webkit-scrollbar': {
    width: 6
  },
  '&::-webkit-scrollbar-track': {
    background: 'transparent'
  },
  '&::-webkit-scrollbar-thumb': {
    background: alpha(theme.palette.grey[400], 0.6),
    borderRadius: 1,
    '&:hover': {
      background: theme.palette.grey[500]
    }
  }
});

export const getWorkOrderBarSubSectionSx = (theme) => ({
  ...getPanelSubSectionSx(theme)
});

// ===== PANEL HEADERS =====

export const getPanelHeaderSx = (theme) => ({
  p: '14px 16px',
  borderBottom: `1px solid ${theme.palette.grey[100]}`,
  background: theme.palette.grey[50]
});

export const getSiteSelectionDivSx = (theme) => ({
  ...getPanelHeaderSx(theme)
});

export const getWorkOrderHeaderSx = (theme) => ({
  ...getPanelHeaderSx(theme),
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  '& > span': {
    fontSize: '14px',
    fontWeight: 600,
    color: theme.palette.grey[900],
    fontFamily: '"Public Sans", sans-serif'
  }
});

// ===== HEADER COMPONENTS =====

export const getLabelMinimizeDivSx = (theme) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  mb: 1.25
});

export const getLabelTextSx = (theme) => ({
  fontSize: '13px',
  fontWeight: 600,
  color: theme.palette.grey[800],
  textTransform: 'uppercase',
  letterSpacing: '0.3px'
});

export const getWorkOrderTitleSx = (theme) => ({
  fontSize: '16px',
  fontWeight: 600,
  color: theme.palette.grey[800],
  textTransform: 'none',
  display: 'flex',
  alignItems: 'center',
  gap: 1
});

export const getLabelIconSx = (theme, isActive = false) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 26,
  height: 26,
  borderRadius: '5px',
  color: theme.palette.grey[600],
  cursor: 'pointer',
  transition: 'background-color 0.2s ease, color 0.2s ease',
  background: isActive ? theme.palette.grey[100] : 'transparent',
  '&:hover': {
    background: theme.palette.grey[100],
    color: theme.palette.grey[800]
  },
  '& svg': {
    width: 16,
    height: 16
  }
});

// ===== BADGES & COUNTS =====

export const getWorkOrderCountSx = (theme) => ({
  fontSize: '14px',
  fontWeight: 400,
  color: theme.palette.text.secondary
});

export const getCountBadgeSx = (theme, variant = 'default') => {
  const variants = {
    risk: {
      background: alpha(theme.palette.error.main, 0.1),
      color: theme.palette.error.main
    },
    average: {
      background: alpha(theme.palette.warning.main, 0.1),
      color: theme.palette.warning.main
    },
    good: {
      background: alpha(theme.palette.success.main, 0.1),
      color: theme.palette.success.main
    },
    default: {
      background: alpha(theme.palette.primary.main, 0.1),
      color: theme.palette.primary.main
    }
  };

  return {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 32,
    height: 28,
    px: 1,
    borderRadius: 1.5,
    fontSize: '14px',
    fontWeight: 700,
    ...(variants[variant] || variants.default)
  };
};

// ===== STATS SECTION =====

export const getTreeDetailsParentSx = (theme) => ({
  p: '14px',
  display: 'flex',
  flexDirection: 'column',
  gap: '14px',
  background: theme.palette.background.paper,
  flexShrink: 0
});

export const getTreeHeadingDivSx = (theme) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  pb: 1.25,
  mb: 1,
  borderBottom: `1px solid ${theme.palette.grey[100]}`
});

export const getTreesTextSx = (theme) => ({
  fontSize: '12px',
  fontWeight: 600,
  color: theme.palette.grey[700],
  textTransform: 'uppercase',
  letterSpacing: '0.5px'
});

export const getStatCardEcologicalSx = (theme) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 1.5,
  p: '12px 14px',
  background: theme.palette.grey[50],
  borderRadius: 2,
  border: `1px solid ${theme.palette.grey[100]}`,
  transition: 'all 0.2s ease',
  '&:hover': {
    background: alpha(theme.palette.primary.main, 0.04),
    borderColor: theme.palette.primary.main,
    boxShadow: `0 0 0 1px ${alpha(theme.palette.primary.main, 0.1)}`
  }
});

export const getStatInfoDivSx = (theme) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 0.25,
  flex: 1,
  minWidth: 0
});

export const getStatLabelSx = (theme) => ({
  fontSize: '11px',
  fontWeight: 500,
  color: theme.palette.grey[600],
  textTransform: 'uppercase',
  letterSpacing: '0.3px'
});

export const getStatValueSx = (theme, size = 'medium') => {
  const sizes = {
    small: '16px',
    medium: '20px',
    large: '24px'
  };

  return {
    fontSize: sizes[size] || sizes.medium,
    fontWeight: 700,
    color: theme.palette.grey[900],
    lineHeight: 1.2
  };
};

export const getStatIconWrapperSx = (theme, variant = 'primary') => {
  const variants = {
    primary: theme.palette.primary.main,
    success: theme.palette.success.main,
    warning: theme.palette.warning.main,
    error: theme.palette.error.main,
    info: theme.palette.info.main
  };

  return {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 40,
    height: 40,
    borderRadius: 2,
    background: alpha(variants[variant] || variants.primary, 0.1),
    color: variants[variant] || variants.primary,
    flexShrink: 0,
    '& svg': {
      width: 20,
      height: 20
    }
  };
};

// ===== HEALTH STATUS SECTION =====

export const getHealthStatusContainerSx = (theme) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(3, 1fr)',
  gap: 1.5,
  mt: 1
});

export const getHealthStatusCardSx = (theme, status = 'good') => {
  const statusConfig = {
    risk: {
      bgColor: alpha(theme.palette.error.main, 0.08),
      borderColor: theme.palette.error.main,
      textColor: theme.palette.error.main,
      iconColor: theme.palette.error.main
    },
    average: {
      bgColor: alpha(theme.palette.warning.main, 0.08),
      borderColor: theme.palette.warning.main,
      textColor: theme.palette.warning.main,
      iconColor: theme.palette.warning.main
    },
    good: {
      bgColor: alpha(theme.palette.success.main, 0.08),
      borderColor: theme.palette.success.main,
      textColor: theme.palette.success.main,
      iconColor: theme.palette.success.main
    }
  };

  const config = statusConfig[status] || statusConfig.good;

  return {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 0.75,
    p: 1.5,
    background: config.bgColor,
    border: `1px solid ${alpha(config.borderColor, 0.3)}`,
    borderRadius: 2,
    transition: 'all 0.2s ease',
    cursor: 'pointer',
    '&:hover': {
      background: alpha(config.borderColor, 0.12),
      borderColor: config.borderColor,
      transform: 'translateY(-2px)',
      boxShadow: `0 4px 8px ${alpha(config.borderColor, 0.2)}`
    }
  };
};

export const getHealthStatusIconSx = (theme, status = 'good') => {
  const statusConfig = {
    risk: theme.palette.error.main,
    average: theme.palette.warning.main,
    good: theme.palette.success.main
  };

  return {
    width: 28,
    height: 28,
    color: statusConfig[status] || statusConfig.good
  };
};

export const getHealthStatusLabelSx = (theme) => ({
  fontSize: '10px',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.3px',
  color: theme.palette.grey[700]
});

export const getHealthStatusCountSx = (theme, status = 'good') => {
  const statusConfig = {
    risk: theme.palette.error.main,
    average: theme.palette.warning.main,
    good: theme.palette.success.main
  };

  return {
    fontSize: '20px',
    fontWeight: 700,
    color: statusConfig[status] || statusConfig.good
  };
};

// ===== WORK ORDER FILTERS =====

export const getWorkOrderFiltersSx = (theme) => ({
  display: 'flex',
  justifyContent: 'space-between',
  gap: 1,
  p: '12px 16px',
  borderBottom: `1px solid ${theme.palette.grey[100]}`,
  background: alpha(theme.palette.background.paper, 0.5),
  '& span': {
    fontSize: '11px',
    fontWeight: 500,
    padding: '4px 8px',
    borderRadius: '4px',
    fontFamily: '"Public Sans", sans-serif'
  },
  '& .open': {
    color: '#a16207',
    backgroundColor: '#fef9c3'
  },
  '& .in-process': {
    color: '#075985',
    backgroundColor: '#e0f2fe'
  },
  '& .closed': {
    color: '#065f46',
    backgroundColor: '#d1fae5'
  }
});

export const getWorkOrderSearchSx = (theme) => ({
  position: 'relative',
  width: '100%',
  '& input': {
    width: '100%',
    p: '10px 12px 10px 36px',
    border: `1px solid ${theme.palette.grey[300]}`,
    borderRadius: 1.5,
    fontSize: '13px',
    color: theme.palette.text.primary,
    background: theme.palette.background.paper,
    transition: 'all 0.2s ease',
    '&:focus': {
      outline: 'none',
      borderColor: theme.palette.primary.main,
      boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.1)}`
    },
    '&::placeholder': {
      color: theme.palette.text.disabled
    }
  }
});

export const getSearchIconSx = (theme) => ({
  position: 'absolute',
  left: 12,
  top: '50%',
  transform: 'translateY(-50%)',
  width: 16,
  height: 16,
  color: theme.palette.grey[500],
  pointerEvents: 'none'
});

// ===== STATUS FILTERS =====

export const getStatusFiltersSx = (theme) => ({
  display: 'flex',
  gap: 1,
  mt: 1.5
});

export const getStatusFilterChipSx = (theme, isActive = false, statusColor = 'primary') => ({
  px: 1.5,
  py: 0.75,
  fontSize: '11px',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.3px',
  borderRadius: 1.5,
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  border: isActive ? `2px solid ${theme.palette[statusColor]?.main || theme.palette.primary.main}` : `2px solid ${theme.palette.grey[200]}`,
  background: isActive ? alpha(theme.palette[statusColor]?.main || theme.palette.primary.main, 0.1) : theme.palette.background.paper,
  color: isActive ? theme.palette[statusColor]?.main || theme.palette.primary.main : theme.palette.grey[600],
  '&:hover': {
    background: alpha(theme.palette[statusColor]?.main || theme.palette.primary.main, 0.15),
    borderColor: theme.palette[statusColor]?.main || theme.palette.primary.main,
    color: theme.palette[statusColor]?.main || theme.palette.primary.main
  }
});

// ===== WORK ORDER LIST =====

export const getWorkOrderListSx = (theme) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 1.5, // 12px spacing between cards
  p: 2, // 16px padding
  flex: 1,
  overflowY: 'auto',
  overflowX: 'hidden',
  '&::-webkit-scrollbar': {
    width: 6
  },
  '&::-webkit-scrollbar-track': {
    background: 'transparent'
  },
  '&::-webkit-scrollbar-thumb': {
    background: alpha(theme.palette.grey[400], 0.6),
    borderRadius: 1,
    '&:hover': {
      background: theme.palette.grey[500]
    }
  }
});

export const getWorkOrderCardSx = (theme, isSelected = false) => ({
  p: '14px 16px',
  borderBottom: `1px solid ${theme.palette.grey[100]}`,
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  background: isSelected ? alpha(theme.palette.primary.main, 0.04) : 'transparent',
  '&:hover': {
    background: alpha(theme.palette.primary.main, 0.06),
    borderLeftColor: theme.palette.primary.main,
    borderLeftWidth: 3,
    pl: '13px' // Adjust padding to compensate for thicker border
  }
});

export const getWorkOrderTitleCardSx = (theme) => ({
  fontSize: '13px',
  fontWeight: 600,
  color: theme.palette.grey[900],
  mb: 0.75,
  lineHeight: 1.3
});

export const getWorkOrderMetaSx = (theme) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: 1.5,
  fontSize: '11px',
  color: theme.palette.grey[600]
});

export const getWorkOrderMetaItemSx = (theme) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 0.5,
  '& svg': {
    width: 12,
    height: 12
  }
});

// ===== SITE CODE & SELECT =====

export const getSiteCodeDivSx = (theme) => ({
  fontSize: '11px',
  color: theme.palette.grey[600],
  fontWeight: 400
});

export const getSelectDivSx = (theme) => ({
  mt: 0
});

// ===== SHOW BUTTON =====

export const getShowButtonSx = (theme) => ({
  mt: '12px !important',
  width: '100%'
});

// ===== LOADING/EMPTY STATES =====

export const getLoadingStateSx = (theme) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  p: 4,
  color: theme.palette.grey[600],
  fontSize: '14px'
});

export const getEmptyStateSx = (theme) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  p: 4,
  gap: 2,
  color: theme.palette.grey[600],
  fontSize: '14px',
  textAlign: 'center'
});

export const getEmptyStateIconSx = (theme) => ({
  width: 48,
  height: 48,
  color: theme.palette.grey[400]
});
